import React, { useEffect, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ExtractedBudget } from "@/lib/offer-types";
import { useExtractOfferBudget } from "@/hooks/use-extract-offer-budget";
import { chunkErrorMessage } from "@/lib/chunk-reload";
import {
  enrichBudgetLines,
  type MatchedBudgetLine,
} from "@/lib/match-offer-line-templates";
import { useOfferLineTemplates } from "@/hooks/use-offers";
import { toast } from "sonner";
import { useUploadOfferFile } from "@/hooks/use-upload-offer-file";
import { OfferPicker } from "./OfferPicker";
import { SrIcon } from "@/components/ui/SrIcon";
import FilePreviewDialog from "@/components/shared/FilePreviewDialog";

export interface MatchedBudget extends Omit<ExtractedBudget, "lines"> {
  lines: MatchedBudgetLine[];
  /** Antall linjer som ikke fant en mal-match — UI kan flagge dette */
  unmatched_count: number;
  /** Indeks i den lokale `uploadedFiles`-arrayet som dette tilbudet ble parsed fra. */
  _sourceFileIdx?: number;
}

interface PdfOfferUploadProps {
  onOfferSelected: (offer: MatchedBudget) => void;
  pdfUploaded: boolean;
  budgetLinesCount: number;
  onReset: () => void;
  aiProgress: number;
  aiLoading: boolean;
}

export function PdfOfferUpload({
  onOfferSelected,
  pdfUploaded,
  budgetLinesCount,
  onReset,
  aiProgress,
  aiLoading,
}: PdfOfferUploadProps) {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [multiOffers, setMultiOffers] = useState<MatchedBudget[]>([]);
  const [showOfferPicker, setShowOfferPicker] = useState(false);
  const [progressLabel, setProgressLabel] = useState<string>("");
  const [previewIdx, setPreviewIdx] = useState<number | null>(null);
  const { data: templates = [] } = useOfferLineTemplates();
  const extractBudget = useExtractOfferBudget();
  const uploadOffer = useUploadOfferFile();

  // Lokale blob-URLer for å forhåndsvise nettopp opplastede filer uten å
  // måtte vente på Supabase Storage-roundtrip. Revoker når komponenten
  // unmountes eller filene byttes ut.
  const previewUrls = useMemo(
    () => uploadedFiles.map((f) => URL.createObjectURL(f)),
    [uploadedFiles],
  );
  useEffect(() => {
    return () => {
      for (const u of previewUrls) URL.revokeObjectURL(u);
    };
  }, [previewUrls]);

  const enrich = (
    offer: ExtractedBudget,
    fileName: string | undefined,
    sourceFileIdx: number,
  ): MatchedBudget => {
    const matched = enrichBudgetLines(offer.lines, templates);
    return {
      ...offer,
      // Prefiks med fil-navn hvis flere filer ble lastet opp så bruker
      // kan skille budsjetter fra ulike kilder i OfferPicker
      name: fileName ? `${fileName} — ${offer.name}` : offer.name,
      lines: matched,
      unmatched_count: matched.filter((l) => l.template_match === null).length,
      _sourceFileIdx: sourceFileIdx,
    };
  };

  const handleFilesUpload = async (files: File[]) => {
    setUploadedFiles(files);
    const allOffers: MatchedBudget[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProgressLabel(
          files.length > 1
            ? `Analyserer fil ${i + 1} av ${files.length} (${file.name})…`
            : `AI analyserer tilbud… (10–30 sek)`,
        );
        // Lagre kopi i offers-bucket for senere referanse (non-blocking).
        // Stille feiler — arkivering skal ikke blokkere parsing.
        uploadOffer.mutate({ file });

        const extracted = await extractBudget.mutateAsync(file);
        const usePrefix = files.length > 1;
        for (const o of extracted) {
          allOffers.push(
            enrich(o, usePrefix ? file.name.replace(/\.[^.]+$/, "") : undefined, i),
          );
        }
      }

      setProgressLabel("");

      if (allOffers.length === 0) {
        toast.error("Ingen budsjettposter funnet i fil(en).");
        return;
      }
      if (allOffers.length > 1) {
        setMultiOffers(allOffers);
        setShowOfferPicker(true);
        toast.info(`${allOffers.length} tilbud funnet. Velg hvilke(t) du vil importere.`);
        return;
      }
      const o = allOffers[0];
      if (o.unmatched_count > 0) {
        toast.warning(
          `${o.unmatched_count} av ${o.lines.length} linjer matchet ikke en mal — sjekk fase manuelt.`,
        );
      }
      onOfferSelected(o);
    } catch (err) {
      setProgressLabel("");
      toast.error(`Feil ved analyse: ${chunkErrorMessage(err)}`);
    }
  };

  const getPreviewForIdx = (idx: number) => {
    const file = uploadedFiles[idx];
    const url = previewUrls[idx];
    if (!file || !url) return null;
    return { url, fileName: file.name, mimeType: file.type || undefined };
  };

  const previewTarget = previewIdx !== null ? getPreviewForIdx(previewIdx) : null;

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-base font-medium">Last opp tilbud</Label>
        <p className="text-sm text-muted-foreground mb-2">
          Last opp tilbuds-PDF eller skjermbilde. Claude Vision (AI) leser
          budsjettet og fyller inn feltene automatisk.
        </p>
        {extractBudget.isPending || aiLoading ? (
          <div className="p-4 border rounded-lg bg-muted/30 space-y-3">
            <div className="flex items-center gap-2">
              <SrIcon name="loader2" className="w-4 h-4 animate-spin text-primary"  />
              <span className="text-sm font-medium">
                {extractBudget.compressing
                  ? "Filen er stor — komprimerer før analyse…"
                  : progressLabel || "AI analyserer tilbud… (10–30 sek)"}
              </span>
            </div>
          </div>
        ) : pdfUploaded ? (
          <div className="flex items-center justify-between p-3 border rounded-lg bg-success/5">
            <Badge variant="secondary" className="bg-success/10 text-success">
              Tilbud analysert, {budgetLinesCount} budsjettposter
            </Badge>
            <div className="flex items-center gap-2">
              {uploadedFiles.length === 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewIdx(0)}
                  type="button"
                >
                  <SrIcon name="eye" className="w-3.5 h-3.5 mr-1" />
                  Vis tilbud
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onReset();
                  setUploadedFiles([]);
                }}
              >
                Last opp nytt
              </Button>
            </div>
          </div>
        ) : (
          <label className="flex items-center gap-2 p-4 border-2 border-dashed rounded-lg hover:bg-muted/30 transition-colors w-full cursor-pointer">
            <SrIcon name="upload" className="w-5 h-5 text-muted-foreground"  />
            <span className="text-sm text-muted-foreground truncate min-w-0 flex-1">
              {uploadedFiles.length > 0
                ? uploadedFiles.length === 1
                  ? uploadedFiles[0].name
                  : `${uploadedFiles.length} filer valgt`
                : "Velg én eller flere PDF-er / skjermbilder…"}
            </span>
            <input
              type="file"
              accept=".pdf,image/png,image/jpeg,image/webp"
              multiple
              className="hidden"
              onChange={e => {
                const files = Array.from(e.target.files ?? []);
                if (files.length > 0) handleFilesUpload(files);
              }}
            />
          </label>
        )}
      </div>

      {showOfferPicker && multiOffers.length > 1 && (
        <OfferPicker
          offers={multiOffers}
          onSelect={offer => {
            onOfferSelected(offer);
            setShowOfferPicker(false);
          }}
          onPreview={(idx) => {
            const fileIdx = multiOffers[idx]?._sourceFileIdx;
            if (fileIdx != null) setPreviewIdx(fileIdx);
          }}
        />
      )}

      {previewTarget && previewIdx !== null && (
        <FilePreviewDialog
          open={previewIdx !== null}
          onOpenChange={(o) => !o && setPreviewIdx(null)}
          url={previewTarget.url}
          fileName={previewTarget.fileName}
          mimeType={previewTarget.mimeType}
          title={previewTarget.fileName}
        />
      )}
    </div>
  );
}
